import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { articlesService } from '../services/articles';
import type { CreateArticleInput, UpdateArticleInput } from '../types/entities';
import { toast } from 'sonner';
import { apiClient } from '../services/api';

export function useArticles(page = 1, limit = 20) {
  return useQuery({
    queryKey: ['articles', page, limit],
    queryFn: () => articlesService.getAll(page, limit),
  });
}

export function useArticleById(id: string) {
  return useQuery({
    queryKey: ['articles', id],
    queryFn: () => articlesService.getById(id),
    enabled: !!id,
  });
}

export function useCreateArticle() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateArticleInput) => articlesService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['articles'] });
      toast.success('Article créé avec succès');
    },
    onError: (error) => {
      toast.error(apiClient.getErrorMessage(error));
    },
  });
}

export function useUpdateArticle() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateArticleInput }) =>
      articlesService.update(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['articles'] });
      queryClient.invalidateQueries({ queryKey: ['articles', variables.id] });
      toast.success('Article mis à jour');
    },
    onError: (error) => {
      toast.error(apiClient.getErrorMessage(error));
    },
  });
}

export function usePublishArticle() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => articlesService.publish(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['articles'] });
      queryClient.invalidateQueries({ queryKey: ['articles', id] });
      toast.success('Article publié');
    },
    onError: (error) => {
      toast.error(apiClient.getErrorMessage(error));
    },
  });
}

export function useUnpublishArticle() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => articlesService.unpublish(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['articles'] });
      queryClient.invalidateQueries({ queryKey: ['articles', id] });
      toast.success('Article dépublié');
    },
    onError: (error) => {
      toast.error(apiClient.getErrorMessage(error));
    },
  });
}

export function useDeleteArticle() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => articlesService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['articles'] });
      toast.success('Article supprimé');
    },
    onError: (error) => {
      toast.error(apiClient.getErrorMessage(error));
    },
  });
}
